import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { groupMembersByOrgAndGroup, getMemberExpandKey } from '../utils/dashboard/memberTreeHelpers';
import MissionKanbanPanel from './MissionKanbanPanel';
import MissionTabsBar from './MissionTabsBar';
import { canEditMissionBoard } from '../utils/permissionControl';
import './MissionMemberPanel.css';
import MissionPlaybackFloatPanel from './MissionPlaybackFloatPanel';
import { downloadMissionMembersKml } from '../utils/dashboard/missionTrackKmlExport';
import { message } from 'antd';

const chipBase = {
  height: 28,
  padding: '0 10px',
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  whiteSpace: 'nowrap',
  border: '1px solid #d0d7de',
  background: '#fff',
  color: '#333',
  lineHeight: 1
};

const chipActive = {
  ...chipBase,
  background: '#1976d2',
  borderColor: '#1976d2',
  color: '#fff',
  fontWeight: 600
};

const popoverPanel = {
  position: 'relative',
  marginTop: 8,
  width: '100%',
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 10,
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  padding: 14,
  zIndex: 40
};

const menuPanel = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: 4,
  minWidth: 148,
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 8,
  boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
  padding: 4,
  zIndex: 41
};

const menuItem = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: 12,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: 4,
  color: '#333'
};

function formatRefreshLabel(ms) {
  const sec = Math.round((Number(ms) || 0) / 1000);
  if (sec < 60) return `每 ${sec} 秒刷新，自动更新`;
  if (sec % 60 === 0) return `每 ${sec / 60} 分钟刷新，自动更新`;
  return `每 ${sec} 秒刷新，自动更新`;
}

function collectMemberIds(list = []) {
  return (list || []).map((m) => getMemberExpandKey(m)).filter(Boolean);
}

/**
 * 任务中：仅勾选树（组/人），不渲染「全部」下的组织状态大树。
 */
function MissionMemberSelectPanel({
  filterMembers = [],
  missionMemberIds = [],
  onConfirm,
  onCancel
}) {
  const [draftSelected, setDraftSelected] = useState(() => new Set(missionMemberIds || []));
  const [filterOrgOpen, setFilterOrgOpen] = useState({});

  const filterOrgMap = useMemo(
    () => groupMembersByOrgAndGroup(filterMembers || []),
    [filterMembers]
  );

  useEffect(() => {
    setDraftSelected(new Set(missionMemberIds || []));
    const orgOpen = {};
    Object.keys(filterOrgMap).forEach((org) => { orgOpen[org] = true; });
    setFilterOrgOpen(orgOpen);
  }, [missionMemberIds, filterOrgMap]);

  const toggleDraftId = (id) => {
    if (!id) return;
    setDraftSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setDraftIds = (ids, checked) => {
    setDraftSelected((prev) => {
      const next = new Set(prev);
      (ids || []).forEach((id) => {
        if (!id) return;
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  return (
    <div style={{
      marginTop: 8,
      width: '100%',
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: 10,
      padding: 14
    }}
    >
      <div style={{
        fontSize: 13,
        fontWeight: 600,
        color: '#1976d2',
        marginBottom: 10,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
      >
        <span>筛选队员（组 / 人）</span>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          style={{ ...chipBase, height: 26 }}
          onClick={() => setDraftIds(collectMemberIds(filterMembers), true)}
        >
          全选
        </button>
        <button
          type="button"
          style={{ ...chipBase, height: 26 }}
          onClick={() => setDraftSelected(new Set())}
        >
          清空
        </button>
        <span style={{ fontSize: 11, color: '#8a93a0', alignSelf: 'center' }}>
          已选 {draftSelected.size} 人
        </span>
      </div>
      <div style={{
        maxHeight: 320,
        overflowY: 'auto',
        border: '1px solid #eef2f6',
        borderRadius: 8,
        padding: 8
      }}
      >
        {Object.keys(filterOrgMap).length === 0 ? (
          <div style={{ fontSize: 12, color: '#999', padding: 8 }}>暂无队员</div>
        ) : (
          Object.entries(filterOrgMap).map(([org, groups]) => {
            const orgMembers = Object.values(groups || {}).flat();
            const orgIds = collectMemberIds(orgMembers);
            const orgChecked = orgIds.length > 0 && orgIds.every((id) => draftSelected.has(id));
            const orgPartial = !orgChecked && orgIds.some((id) => draftSelected.has(id));
            return (
              <div key={org} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => setFilterOrgOpen((p) => ({ ...p, [org]: !(p[org] !== false) }))}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      padding: 0,
                      width: 16,
                      color: '#666'
                    }}
                  >
                    {filterOrgOpen[org] !== false ? '▾' : '▸'}
                  </button>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
                    <input
                      type="checkbox"
                      checked={orgChecked}
                      ref={(el) => { if (el) el.indeterminate = orgPartial; }}
                      onChange={(e) => setDraftIds(orgIds, e.target.checked)}
                    />
                    {org}
                    <span style={{ fontWeight: 400, color: '#8a93a0' }}>({orgMembers.length})</span>
                  </label>
                </div>
                {filterOrgOpen[org] !== false && Object.entries(groups || {}).map(([group, list]) => {
                  const gIds = collectMemberIds(list);
                  const gChecked = gIds.length > 0 && gIds.every((id) => draftSelected.has(id));
                  const gPartial = !gChecked && gIds.some((id) => draftSelected.has(id));
                  return (
                    <div key={`${org}-${group}`} style={{ marginLeft: 22, marginTop: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: '#444' }}>
                        <input
                          type="checkbox"
                          checked={gChecked}
                          ref={(el) => { if (el) el.indeterminate = gPartial; }}
                          onChange={(e) => setDraftIds(gIds, e.target.checked)}
                        />
                        {group}
                        <span style={{ color: '#8a93a0' }}>({list.length})</span>
                      </label>
                      <div style={{ marginLeft: 20, marginTop: 4 }}>
                        {(list || []).map((m) => {
                          const id = getMemberExpandKey(m);
                          return (
                            <label
                              key={id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                cursor: 'pointer',
                                fontSize: 12,
                                padding: '3px 0',
                                color: '#333'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={draftSelected.has(id)}
                                onChange={() => toggleDraftId(id)}
                              />
                              {m.name || id}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => onCancel?.()}
          style={{
            height: 30,
            padding: '0 14px',
            background: '#fff',
            color: '#555',
            border: '1px solid #d0d7de',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 12
          }}
        >
          取消
        </button>
        <button
          type="button"
          onClick={() => onConfirm?.(Array.from(draftSelected))}
          style={{
            height: 30,
            padding: '0 14px',
            background: '#1976d2',
            color: '#fff',
            border: '1px solid #1976d2',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 500
          }}
        >
          确认（进入任务中）
        </button>
      </div>
      <div style={{ fontSize: 11, color: '#8a93a0', marginTop: 8 }}>
        确认后地图只显示勾选队员；点「全部」可恢复显示全体组织树与地图点位。
      </div>
    </div>
  );
}

/**
 * 指挥页队员侧栏：
 * - 最顶常显「筛选与显示」（隐藏无效 / 只显示在线），全局生效、不进标签
 * - 「全部」：仅由外层渲染组织状态大树
 * - 「任务中」：本组件内仅渲染勾选列表（组/人），不混入大树
 */
export default function DashboardMemberToolbar({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  onResetConfig,
  onReplayEarth,
  showMemberNames,
  onToggleMemberNames,
  onShowOnlineTracks,
  onDrawOnlineTracks,
  hideInvalid,
  showOnlineOnly,
  onHideInvalidChange,
  onShowOnlineOnlyChange,
  filteredCount = 0,
  totalCount = 0,
  onlineCount = 0,
  refreshInterval,
  onRefreshIntervalChange,
  networkQuality = 'good',
  trackModeAndPlaybackUI,
  commandFeatures = {},
  searchQuery = '',
  onSearchQueryChange,
  onExportKml,
  showDownload = false,
  onToggleAllTracks,
  allTracksVisible = false,
  filterMembers = [],
  missionMode = false,
  missionMemberIds = [],
  onMissionModeChange,
  onMissionMemberIdsChange,
  /** 'all' | 'mission' | 'playback' */
  panelTab = 'all',
  onPanelTabChange,
  missionPickIds = [],
  onMissionPickIdsChange,
  onJoinMission,
  missionGroups = [],
  onMissionGroupsChange,
  onRemoveMissionMember,
  onEndMission,
  missions = [],
  activeMissionId = null,
  onSelectMission,
  onCreateMission,
  onRenameMission,
  onCloseMission,
  onToggleMissionRecordTracks,
  missionMembers = [],
  /** Unique live people across all open missions (member-side copy / subtitle); falls back to missionMembers.length. Tab「任务中」badge uses missions.length. */
  missionBadgeCount = null,
  isMissionMemberOnline,
  getMissionMemberPresence,
  onFlyToMissionMember,
  voicePulseIds = null,
  streamingMembers = null,
  openLiveStream = null,
  getMessagesForMember,
  onMissionChatOutbound,
  onMissionChatOpenChange,
  onMissionChatMessageDeleted = null,
  notesByKey = null,
  onSaveMemberNote = null,
  onSendLocationToMember = null,
  onSendMessageToMember = null,
  historyEpoch = 0,
  getBasemapId = null,
  playbackTargetNames = [],
  onPlaybackTargetNamesChange,
  playbackSpeed = 10,
  onPlaybackSpeedChange,
  trackData = {},
  startRealTimeTrackDrawing = null,
  stopTrackFeatures = null,
  realTimeTrackDrawing = null,
  trackColors = {},
  showTracks = {},
  onToggleTrack,
  getMap,
  // 完整回放控件（可选；无则用 trackModeAndPlaybackUI）
  playbackPanelUI = null
}) {
  const canViewTrack = commandFeatures.viewTrack !== false;
  const canRealtimeDraw = commandFeatures.realtimeDraw !== false;
  const canPlayback = commandFeatures.playback !== false && !!trackModeAndPlaybackUI;

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [trackMenuOpen, setTrackMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [missionPlaybackOpen, setMissionPlaybackOpen] = useState(false);
  const rootRef = useRef(null);

  const closeMenus = () => {
    setSettingsOpen(false);
    setTrackMenuOpen(false);
    setMoreOpen(false);
  };

  useEffect(() => {
    if (!settingsOpen && !trackMenuOpen && !moreOpen) return undefined;
    const onDown = (e) => {
      if (rootRef.current?.contains?.(e.target)) return;
      closeMenus();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') closeMenus();
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [settingsOpen, trackMenuOpen, moreOpen]);

  const networkTip = useMemo(() => (
    networkQuality === 'poor'
      ? '网络较差，建议使用 2–5 分钟刷新间隔'
      : networkQuality === 'fair'
        ? '网络一般，建议使用 1–2 分钟刷新间隔'
        : '网络良好，可使用较短刷新间隔'
  ), [networkQuality]);

  const titleCount = totalCount || filteredCount;
  const titleOnline = onlineCount;
  const [searchOpen, setSearchOpen] = useState(() => Boolean(searchQuery));
  useEffect(() => {
    if (searchQuery) setSearchOpen(true);
  }, [searchQuery]);
  // Same as MissionKanban / missionMembers: live people, not raw missionMemberIds.length (ghosts)
  const missionCount = (missionBadgeCount == null
    ? (Array.isArray(missionMembers) ? missionMembers.length : 0)
    : Number(missionBadgeCount) || 0);
  /** Tab「任务中」badge: open mission count (not member count) */
  const openMissionCount = Array.isArray(missions) ? missions.length : 0;
  const isMissionTab = panelTab === 'mission';
  const isPlaybackTab = panelTab === 'playback';
  const isAllTab = panelTab === 'all';
  const canEditBoard = canEditMissionBoard();

  const goAll = () => {
    onPanelTabChange?.('all');
    onMissionModeChange?.(false);
  };

  const goMission = () => {
    onPanelTabChange?.('mission');
    onMissionModeChange?.(true);
  };

  const goPlayback = () => {
    // 全部队员回放：预勾选所有已有轨迹数据的队员（与任务中回放的任务名册范围区分）
    const td = trackData || {};
    const names = Object.keys(td).filter((k) => Array.isArray(td[k]) && td[k].length > 0);
    onPlaybackTargetNamesChange?.(names);
    setMissionPlaybackOpen(false);
    setSettingsOpen(false);
    setTrackMenuOpen(false);
    setMoreOpen(false);
    onPanelTabChange?.('playback');
  };

  useEffect(() => {
    if (!isMissionTab) setMissionPlaybackOpen(false);
  }, [isMissionTab]);

  const missionTrackMembers = useMemo(() => {
    const roster = Array.isArray(missionMembers) ? missionMembers : [];
    return roster.filter((m) => m && m.name && Array.isArray(trackData?.[m.name]) && trackData[m.name].length > 0);
  }, [missionMembers, trackData]);

  const activeMissionName = useMemo(() => {
    const list = Array.isArray(missions) ? missions : [];
    const hit = list.find((m) => m.id === activeMissionId);
    return hit?.name || '当前任务';
  }, [missions, activeMissionId]);

  const isMemberDrawing = useCallback((name) => {
    if (!name) return false;
    if (realTimeTrackDrawing?.has) return realTimeTrackDrawing.has(name);
    if (realTimeTrackDrawing && typeof realTimeTrackDrawing === 'object') return !!realTimeTrackDrawing[name];
    return false;
  }, [realTimeTrackDrawing]);

  const exportActiveMissionKml = useCallback((mission) => {
    const m = mission || (Array.isArray(missions) ? missions.find((x) => x.id === activeMissionId) : null);
    if (!m) {
      message.info('请先选择任务');
      return;
    }
    const roster = Array.isArray(missionMembers) ? missionMembers : [];
    const result = downloadMissionMembersKml({
      missionName: m.name || '任务',
      members: roster,
      trackData: trackData || {}
    });
    if (!result.ok) {
      message.info(result.message || '导出失败');
      return;
    }
    message.success(`已导出 ${result.count} 条队员轨迹 KML`);
  }, [missions, activeMissionId, missionMembers, trackData]);

  const openMissionPlayback = useCallback(() => {
    const names = missionTrackMembers.map((m) => m.name).filter(Boolean);
    onPlaybackTargetNamesChange?.(names);
    setSettingsOpen(false);
    setTrackMenuOpen(false);
    setMoreOpen(false);
    setMissionPlaybackOpen(true);
    if (panelTab !== 'mission') {
      onPanelTabChange?.('mission');
      onMissionModeChange?.(true);
    }
  }, [
    missionTrackMembers,
    onPlaybackTargetNamesChange,
    panelTab,
    onPanelTabChange,
    onMissionModeChange
  ]);

  const confirmMission = (ids) => {
    const list = Array.isArray(ids) ? ids : [];
    onMissionMemberIdsChange?.(list);
    if (list.length === 0) {
      onMissionModeChange?.(false);
    } else {
      onMissionModeChange?.(true);
    }
    onPanelTabChange?.('mission');
  };

  return (
    <div ref={rootRef} className={isMissionTab ? 'mission-member-panel' : undefined} style={{ marginBottom: 12, position: 'relative' }}>
      {/* 最顶：两项横排全局勾选（PITFALLS §6.9/6.10；1c6b7d6 搜索改版时误删，现恢复） */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '2px 12px',
        padding: '0 4px',
        marginBottom: 4,
        marginTop: 0,
        fontSize: 12,
        color: '#333',
        lineHeight: 1.3
      }}
      >
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input
            type="checkbox"
            checked={!!hideInvalid}
            onChange={(e) => onHideInvalidChange?.(e.target.checked)}
            style={{ margin: 0 }}
          />
          隐藏无效队员
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input
            type="checkbox"
            checked={!!showOnlineOnly}
            onChange={(e) => onShowOnlineOnlyChange?.(e.target.checked)}
            style={{ margin: 0 }}
          />
          只显示在线队员
        </label>
      </div>

      {/* title + search icon */}
      <div className="mission-panel-heading" style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: searchOpen ? 8 : 10,
        paddingBottom: 8,
        borderBottom: '2px solid #1976d2'
      }}
      >
        <span style={{ fontSize: 16, fontWeight: 700, color: '#1976d2' }}>队员</span>
        <span style={{ fontSize: 12, color: '#666', flex: 1 }}>
          {titleCount}人 · {titleOnline}在线
          {missionMode ? ` · 任务中 ${missionCount}` : ''}
        </span>
        <button
          type="button"
          onClick={() => setSearchOpen((v) => !v)}
          title="搜索"
          style={{
            border: 'none',
            background: searchOpen ? '#e3f2fd' : 'transparent',
            color: '#1976d2',
            cursor: 'pointer',
            width: 28,
            height: 28,
            borderRadius: 6,
            fontSize: 16,
            lineHeight: 1,
            flexShrink: 0
          }}
        >
          ⌕
        </button>
      </div>

      {searchOpen ? (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        height: 34,
        padding: '0 10px',
        marginBottom: 10,
        border: '1px solid #d0d7de',
        borderRadius: 8,
        background: '#fff'
      }}
      >
        <span style={{ color: '#8a93a0', fontSize: 14, lineHeight: 1 }} aria-hidden>⌕</span>
        <input
          type="search"
          autoFocus
          value={searchQuery}
          onChange={(e) => onSearchQueryChange?.(e.target.value)}
          placeholder="搜索姓名 / 组织 / 小组"
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            fontSize: 13,
            background: 'transparent',
            minWidth: 0
          }}
        />
        {!!searchQuery && (
          <button
            type="button"
            onClick={() => onSearchQueryChange?.('')}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#999',
              cursor: 'pointer',
              fontSize: 14,
              padding: 0,
              lineHeight: 1
            }}
            title="清除搜索"
          >
            ×
          </button>
        )}
      </div>
      ) : null}

      {/* 标签条：全部 | 任务中 | 轨迹 | 显示设置（无独立「筛选」叠层，避免与任务中混） */}
      <div className="mission-panel-tabs" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 6 }}>
        <button
          type="button"
          className={isAllTab ? 'mission-panel-tab is-active' : 'mission-panel-tab'}
          style={isAllTab ? chipActive : chipBase}
          onClick={goAll}
          title="显示完整组织树队员列表"
        >
          全部 {titleCount}
        </button>
        <button
          type="button"
          className={isMissionTab ? 'mission-panel-tab is-active' : 'mission-panel-tab'}
          style={isMissionTab ? chipActive : chipBase}
          onClick={goMission}
          title="任务中：数字为当前任务数；待编组池与临时分组；地图只显示任务队员"
        >
          任务中{openMissionCount > 0 ? ` ${openMissionCount}` : ''}
        </button>


        <button
          type="button"
          style={settingsOpen ? chipActive : chipBase}
          onClick={() => {
            setTrackMenuOpen(false);
            setMoreOpen(false);
            setSettingsOpen((v) => !v);
          }}
          title="显示设置"
        >
          <span aria-hidden>⚙</span> 显示设置
        </button>

        <div style={{ position: 'relative', marginLeft: 'auto' }}>
          <button
            type="button"
            style={{ ...chipBase, padding: '0 8px', minWidth: 28, justifyContent: 'center' }}
            onClick={() => {
              setSettingsOpen(false);
              setTrackMenuOpen(false);
              setMoreOpen((v) => !v);
            }}
            title="更多"
          >
            ···
          </button>
          {moreOpen && (
            <div style={{ ...menuPanel, left: 'auto', right: 0 }} role="menu">
              {canPlayback && (
                <button
                  type="button"
                  style={menuItem}
                  onClick={() => { goPlayback(); }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f7fa'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  title="轨迹回放：预勾选全部已有轨迹的队员；多选目标、时间轴、倍速"
                >
                  回放
                </button>
              )}
              <button
                type="button"
                style={menuItem}
                onClick={() => { setMoreOpen(false); onReplayEarth?.(); }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f7fa'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                查看地球
              </button>
              <button
                type="button"
                style={menuItem}
                onClick={() => { setMoreOpen(false); onResetConfig?.(); }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f7fa'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                恢复默认筛选
              </button>
              {canViewTrack && (
                <button
                  type="button"
                  style={menuItem}
                  onClick={() => { setMoreOpen(false); onShowOnlineTracks?.(); }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f7fa'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  显示在线轨迹
                </button>
              )}
              {canRealtimeDraw && (
                <button
                  type="button"
                  style={menuItem}
                  onClick={() => { setMoreOpen(false); onDrawOnlineTracks?.(); }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f7fa'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  绘制在线轨迹
                </button>
              )}
              {canViewTrack && onToggleAllTracks && (
                <button
                  type="button"
                  style={menuItem}
                  onClick={() => { setMoreOpen(false); onToggleAllTracks?.(); }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f7fa'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {allTracksVisible ? '隐藏全部轨迹' : '显示全部轨迹'}
                </button>
              )}
              {showDownload && (
                <button
                  type="button"
                  style={menuItem}
                  onClick={() => { setMoreOpen(false); onExportKml?.(); }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f7fa'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  导出 KML
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {!isMissionTab && (
      <div style={{ fontSize: 11, color: '#8a93a0', marginBottom: settingsOpen || isAllTab ? 8 : 0 }}>
        {formatRefreshLabel(refreshInterval)}
        <span style={{ marginLeft: 8 }}>
          {`当前显示 ${filteredCount} 人`}
        </span>
      </div>
      )}

      {/* 全部页：勾选后加入任务中；显示设置打开时收起，避免与设置弹层混叠 */}
      {!settingsOpen && isAllTab && canEditBoard && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 8,
          padding: '6px 8px',
          background: '#f5f8fc',
          border: '1px solid #e3ebf3',
          borderRadius: 8
        }}
        >
          <span style={{ fontSize: 12, color: '#555' }}>
            已勾选 {Array.isArray(missionPickIds) ? missionPickIds.length : 0} 人
            {missionCount > 0 ? ` · 任务中 ${missionCount}` : ''}
          </span>
          <button
            type="button"
            disabled={!Array.isArray(missionPickIds) || missionPickIds.length === 0}
            onClick={() => onJoinMission?.(missionPickIds)}
            style={{
              height: 28,
              padding: '0 12px',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 6,
              border: '1px solid #1976d2',
              background: (!Array.isArray(missionPickIds) || missionPickIds.length === 0) ? '#c5d8ef' : '#1976d2',
              color: '#fff',
              cursor: (!Array.isArray(missionPickIds) || missionPickIds.length === 0) ? 'not-allowed' : 'pointer'
            }}
            title="将勾选队员加入任务中（开放语音对讲）"
          >
            加入任务中
          </button>
          {Array.isArray(missionPickIds) && missionPickIds.length > 0 && (
            <button
              type="button"
              onClick={() => onMissionPickIdsChange?.([])}
              style={{
                height: 28,
                padding: '0 10px',
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid #d0d7de',
                background: '#fff',
                color: '#555',
                cursor: 'pointer'
              }}
            >
              清空勾选
            </button>
          )}
        </div>
      )}

      {settingsOpen && (
        <div style={popoverPanel}>
          <div style={{
            fontSize: 13,
            fontWeight: 600,
            color: '#1976d2',
            marginBottom: 10,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
          >
            <span>显示设置</span>
            <button
              type="button"
              onClick={() => setSettingsOpen(false)}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#999', fontSize: 16, lineHeight: 1 }}
              aria-label="关闭"
            >
              ×
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ color: '#555', minWidth: 64 }}>刷新间隔</span>
              <select
                value={refreshInterval / 1000}
                onChange={(e) => onRefreshIntervalChange?.(parseInt(e.target.value, 10) * 1000)}
                style={{
                  height: 28,
                  padding: '0 8px',
                  border: '1px solid #d0d7de',
                  borderRadius: 6,
                  fontSize: 12,
                  minWidth: 96
                }}
              >
                <option value={5}>5 秒</option>
                <option value={10}>10 秒</option>
                <option value={20}>20 秒</option>
                <option value={30}>30 秒</option>
                <option value={60}>1 分钟</option>
                <option value={120}>2 分钟</option>
                <option value={300}>5 分钟</option>
                <option value={600}>10 分钟</option>
              </select>
            </div>
            <div style={{ fontSize: 11, color: '#8a93a0' }}>{networkTip}</div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ color: '#555' }}>显示队员名字</span>
              <button
                type="button"
                role="switch"
                aria-checked={!!showMemberNames}
                onClick={() => onToggleMemberNames?.()}
                style={{
                  width: 44,
                  height: 24,
                  borderRadius: 12,
                  border: 'none',
                  cursor: 'pointer',
                  background: showMemberNames ? '#1976d2' : '#c5cdd6',
                  position: 'relative',
                  transition: 'background 0.2s',
                  padding: 0
                }}
                title={showMemberNames ? '点击隐藏地图上的队员名字' : '点击显示地图上的队员名字'}
              >
                <span style={{
                  position: 'absolute',
                  top: 2,
                  left: showMemberNames ? 22 : 2,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  transition: 'left 0.2s'
                }}
                />
              </button>
            </div>

            <div style={{ borderTop: '1px solid #eee', paddingTop: 10 }}>
              <div style={{ color: '#555', marginBottom: 6, fontWeight: 500 }}>队员时间筛选</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ minWidth: 56, color: '#666' }}>开始时间</span>
                  <input
                    type="datetime-local"
                    value={startTime || ''}
                    onChange={(e) => onStartTimeChange?.(e.target.value)}
                    style={{
                      flex: 1,
                      height: 28,
                      padding: '0 8px',
                      border: '1px solid #d0d7de',
                      borderRadius: 6,
                      fontSize: 12,
                      minWidth: 0
                    }}
                  />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ minWidth: 56, color: '#666' }}>结束时间</span>
                  <input
                    type="datetime-local"
                    value={endTime || ''}
                    onChange={(e) => onEndTimeChange?.(e.target.value)}
                    style={{
                      flex: 1,
                      height: 28,
                      padding: '0 8px',
                      border: '1px solid #d0d7de',
                      borderRadius: 6,
                      fontSize: 12,
                      minWidth: 0
                    }}
                  />
                </label>
              </div>
            </div>

            {canPlayback && (
              <div style={{ borderTop: '1px solid #eee', paddingTop: 10 }}>
                <div style={{ color: '#555', marginBottom: 6, fontWeight: 500 }}>轨迹范围 / 回放</div>
                {trackModeAndPlaybackUI}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 4 }}>
              <button
                type="button"
                onClick={() => { setSettingsOpen(false); onReplayEarth?.(); }}
                style={{
                  height: 30,
                  padding: '0 12px',
                  background: '#1976d2',
                  color: '#fff',
                  border: '1px solid #1976d2',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 500
                }}
              >
                🌐 查看地球
              </button>
              <button
                type="button"
                onClick={() => { onResetConfig?.(); }}
                style={{
                  height: 30,
                  padding: '0 12px',
                  background: '#fff',
                  color: '#1976d2',
                  border: '1px solid #1976d2',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 500
                }}
                title="恢复默认筛选（原「导入配置」）"
              >
                导入配置
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 任务中：上下栏看板；显示设置打开时收起，避免与设置弹层混叠 */}
      {!settingsOpen && isMissionTab && (
        <div style={{ position: 'relative', minHeight: 200 }}>
        <MissionTabsBar
          missions={missions}
          activeMissionId={activeMissionId}
          liveMembers={(Array.isArray(filterMembers) && filterMembers.length)
            ? filterMembers
            : (missionMembers || [])}
          getMemberPresence={getMissionMemberPresence}
          onSelectMission={onSelectMission}
          onCreateMission={onCreateMission}
          onRenameMission={onRenameMission}
          onCloseMission={onCloseMission}
          onToggleMissionRecordTracks={onToggleMissionRecordTracks}
          onOpenMissionPlayback={canPlayback ? openMissionPlayback : undefined}
          onExportMissionKml={exportActiveMissionKml}
          canPlayback={canPlayback}
          participantCount={Array.isArray(missionMembers) ? missionMembers.length : 0}
        />

        <MissionKanbanPanel
          missionName={activeMissionName || null}
          missionMembers={missionMembers}
          missionGroups={missionGroups}
          onGroupsChange={onMissionGroupsChange}
          onRemoveMember={onRemoveMissionMember}
          onEndMission={onEndMission}
          endMissionEnabled={Array.isArray(missions) ? missions.length > 0 : undefined}
          getMemberPresence={getMissionMemberPresence}
          onFlyToMember={onFlyToMissionMember}
          voicePulseIds={voicePulseIds}
          streamingMembers={streamingMembers}
          openLiveStream={openLiveStream}
          getMessagesForMember={getMessagesForMember}
          onOutboundSent={onMissionChatOutbound}
          onChatOpenChange={onMissionChatOpenChange}
          onMessageDeleted={onMissionChatMessageDeleted}
          getMap={getMap}
          getBasemapId={getBasemapId}
          historyEpoch={historyEpoch}
          notesByKey={notesByKey}
          onSaveMemberNote={onSaveMemberNote}
          onSendLocationToMember={onSendLocationToMember}
          onSendMessageToMember={onSendMessageToMember}
          isDrawing={isMemberDrawing}
          startRealTimeTrackDrawing={startRealTimeTrackDrawing}
          stopTrackFeatures={stopTrackFeatures}
          showTracks={showTracks}
          toggleTrack={onToggleTrack}
        />
        <MissionPlaybackFloatPanel
          open={missionPlaybackOpen && canPlayback}
          title={"任务轨迹回放"}
          subtitle={activeMissionName + (missionTrackMembers.length ? (' \u00b7 ' + missionTrackMembers.length + ' \u4eba\u6709\u8f68\u8ff9') : '')}
          empty={missionTrackMembers.length === 0}
          emptyText={"该任务暂无已加载的队员轨迹。可先在「显示设置」或 ··· 菜单「回放」中应用轨迹时间范围，再回到本任务点回放。"}
          onClose={() => setMissionPlaybackOpen(false)}
        >
          {playbackPanelUI
            ? React.cloneElement(playbackPanelUI, {
                variant: 'full',
                members: missionTrackMembers
              })
            : (trackModeAndPlaybackUI
              ? React.cloneElement(trackModeAndPlaybackUI, {
                  variant: 'full',
                  members: missionTrackMembers
                })
              : null)}
        </MissionPlaybackFloatPanel>
        </div>
      )}

      {!settingsOpen && isPlaybackTab && canPlayback && (
        <div style={{
          marginTop: 8,
          width: '100%',
          background: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: 10,
          padding: 14
        }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1976d2', marginBottom: 10 }}>
            轨迹回放
          </div>
          {playbackPanelUI || trackModeAndPlaybackUI}
        </div>
      )}
    </div>
  );
}
